<template>
  <div class="calculate pad">
    <div class="title">
      <p>{{ $t('calculate-value-for-year', { year: $config.year }) }}</p>
      <h3>{{ $t('cta-calculate') }}</h3>
    </div>
    <div class="recap">
      <p class="tx-wg-bold table-header">{{ $t('recap-label') }}</p>
      <div class="blocks">

        <!-- Fonti per le attività culturali -->
        <div class="block" @mouseover="setActive(funds)" @mouseleave="setActive(null)" @click="setActive(funds)"
          :class="activeClass('funds')">
          <p class="sign tx-wg-bold" :style="{ color: funds.color }">&#43;</p>
          <div class="block-info">
            <p :style="{ width: funds.perc + '%', backgroundColor: funds.color }" class="tx-wg-bold">
              {{ funds.value | currency }}
            </p>
          </div>
        </div>

        <!-- Costi di struttura -->
        <div class="block" @mouseover="setActive(costs)" @mouseleave="setActive(null)" @click="setActive(costs)"
          :class="activeClass('costs')">
          <p class="sign tx-wg-bold" :style="{ color: costs.color }">&#8722;</p>
          <div class="block-info">
            <p :style="{
              width: $device.mobile ? '35%' : costs.perc + '%',
              backgroundColor: costs.color
            }" class="tx-wg-bold">
              {{ costs.value | neutralCurrency }}
            </p>
          </div>
        </div>

        <!-- Valore aggiunto attività future e avanzo -->
        <div class="block total" @mouseover="setActive(total.addedValue[1])" @mouseleave="setActive(null)"
          @click="setActive(total.addedValue[1])" :class="activeClass('accantonamento')">
          <p class="sign tx-wg-bold" :style="{ color: total.color }">&#8722;</p>
          <div class="block-info">
            <p :style="{
              width: $device.mobile ? '65%' : total.perc + '%',
              marginLeft: $device.mobile ? '35%' : costs.perc + '%',
              backgroundColor: total.color
            }" class="tx-wg-bold offset">
              {{ total.addedValue[1].value | currency }}
            </p>
          </div>
        </div>

        <!-- Line --------------- -->
        <div class="line"></div>
        <!-- Line --------------- -->

        <!-- Valore aggiunto distribuito -->
        <div class="block" @click="setActive(total.addedValue[0])" @mouseover="setActive(total.addedValue[0])"
          @mouseleave="setActive(null)" :class="activeClass('total-calculated')" :style="{ color: total.color }">
          <p class="sign tx-wg-bold">&#61;</p>
          <div class="block-info ">
            <p :style="{
              width: $device.mobile ? '100%' : (100 - total.perc - costs.perc)+'%',
              backgroundColor: total.color
            }" class="tx-wg-bold total">
              {{ total.addedValue[0].value | currency }}
            </p>
          </div>
        </div>

      </div>
      <div class="block-description">
        <p v-if="!currentActive">
          {{ $t('cta-interaction') }}
        </p>
        <p v-else class="tx-wg-bold" :style="{ color: currentActive.color }">
          {{ currentActive.title[$i18n.locale] }}
        </p>
        <p v-if="currentActive">
          {{ currentActive.description[$i18n.locale] }}
        </p>
      </div>
    </div>
    <!-- Recap End -->

    <!-- Table -->
    <div class="table-header">
      <p class="tx-wg-bold">
        {{ $t('elaborate-label') }}
      </p>
      <mq-layout :mq="['xsm', 'sm']" class="year mobile">
        <div class="prev" @click="switchTableYear">
          <svg width="9" height="12" viewBox="0 0 9 12" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M1 10.88l6.123-4.936L1 1" stroke="#fff" stroke-width="1.5" stroke-miterlimit="10"/></svg>
        </div>
        <p v-if="currentTableYear === $config.year" class="tx-wg-bold">{{ $config.year }}</p>
        <p v-if="currentTableYear === $config.year - 1" class="tx-wg-bold">
          {{ $config.year - 1 }}
        </p>
        <div class="next" @click="switchTableYear">
          <svg width="9" height="12" viewBox="0 0 9 12" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M1 10.88l6.123-4.936L1 1" stroke="#fff" stroke-width="1.5" stroke-miterlimit="10"/></svg>
        </div>
      </mq-layout>
      <mq-layout mq="tb+" class="year">
        <p class="tx-wg-bold">{{ $config.year }}</p>
        <p class="tx-wg-bold">{{ $config.year - 1 }}</p>
      </mq-layout>
    </div>
    <Table :data="currentTableYear ? values[currentTableYear] : values" />
    <!-- <p class="tx-body-small">{{ $t('calculate-table-bottom-note') }}</p> -->
    <Attachments :title="$t('attachments')" :files="attachments" />

    <!-- Table -->
  </div>
</template>

<script>
import attachments from '@/assets/data/attachments';
import Attachments from '@/components/common/Attachments';
import Table from '@/components/common/Table';
import * as scale from 'd3-scale';

export default {
  name: 'Calculate',
  components: {
    Table,
    Attachments
  },
  data() {
    return {
      attachments: attachments['added-value'],
      tilt: this.$device.mobile ? 1.12 : 1.1,
      currentActive: null,
      currentTableYear: null
    };
  },
  computed: {
    values() {
      return this.$store.getters['values/list'];
    },
    currentValue() {
      return this.values[this.$config.year];
    },
    funds() {
      const v = this.currentValue.find(v => v.key === 'funds');
      return {
        ...v,
        perc: 100
      };
    },
    costs() {
      const v = this.currentValue.find(v => v.key === 'costs');
      return {
        ...v,
        perc: 16
      };
    },
    total() {
      const v = this.currentValue.find(v => v.key === 'total');
      return {
        ...v,
        addedValue: v.items.filter(v => v.color),
        perc: 52
      };
    }
  },
  mounted() {
    if (!this.$device.mobile) return;
    this.currentTableYear = this.$config.year;
  },
  methods: {
    setActive(el) {
      this.currentActive = el;
    },
    switchTableYear() {
      if (this.currentTableYear === this.$config.year - 1)
        this.currentTableYear = this.$config.year;
      else this.currentTableYear = this.$config.year - 1;
    },
    activeClass(key) {
      if (!this.currentActive) return 'active';
      if (this.currentActive.key === key) return 'active';
      return 'disable';
    }
  }
};
</script>

<style lang="scss" scoped>
.calculate {
  width: 100%;
  color: $col-navy-dark;
  padding-bottom: $padding/2;

  .recap {
    margin-bottom: 2rem;

    @media screen and (max-width: $mq-mobile) {
      margin-bottom: 1rem;
    }

    .blocks {
      margin: 1rem 0;
      width: 100%;
      display: flex;
      flex-direction: column;

      .line {
        border: 1px solid $col-navy-dark;
        width: 100%;
        margin: 0.5rem 0;
      }

      .block {
        @include centered(false, false, row);
        height: 3rem;
        transition: opacity 400ms;
        margin: 0.5rem 0;

        @media screen and (max-width: $mq-mobile) {
          height: 2.5rem;
          margin: 0;
        }

        &:hover {
          cursor: pointer;
        }

        &.disable {
          opacity: 0.2;
        }

        &.active {
          opacity: 1;
        }

        .sign {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          font-size: 3rem;
          min-width: 4rem;

          @media screen and (max-width: $mq-mobile) {
            font-size: 1.5rem;
            min-width: 2rem;
          }
        }

        &.total {
          .block-info p {
            flex: 1;
            width: auto;
          }
        }

        .block-info {
          @include centered(false, true, column);
          display: flex;
          flex-direction: column;
          width: 100%;

          p {
            color: $col-white;
            padding: 1rem;
            border-radius: 10px;

            @media screen and (max-width: $mq-mobile) {
              padding: 0.5rem 0.4rem;
            }

            &.total {
              margin-left: auto;

              @media screen and (max-width: $mq-tablet) {
                margin-left: 0;
              }
            }
          }
        }
      }
    }
  }

  .title {
    margin-bottom: 2rem;

    @media screen and (max-width: $mq-mobile) {
      margin-bottom: 1rem;
    }

    h3 {
      color: $col-red-light;
    }
  }

  .table-header {
    text-transform: uppercase;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-bottom: 0.2rem;

    @media screen and (max-width: $mq-mobile) {
      margin-bottom: 1rem;
    }

    p {
      width: 60%;
    }

    .year {
      flex: 1;
      display: flex;
      flex-direction: row;
      width: 20%;
      justify-content: space-between;
      text-align: right;

      p {
        margin-right: 1rem;
      }

      &.mobile {

        .prev,
        .next {
          width: 20%;

          &:hover {
            cursor: pointer;
          }
        }

        .prev {
          transform: scale(-1, 1);
        }

        svg path {
          stroke: $col-navy-dark;
        }

        p {
          margin: 0;
          text-align: center;
        }
      }
    }
  }

  .block-description {
    min-height: 5rem;
  }
}
</style>
